'use client';

import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { useForm, schemaResolver } from '@mantine/form';
import FormControl from '../Form/FormControl';
import TextInput from '../Form/TextInput';
import Button from '../Form/Button';
import Form from '../Form/Form';
import useLoadTracker from '../../Hooks/useLoadTracker';
import { z } from 'zod';
import Center from '../Misc/Center';
import { JsonApiError } from '../../Utils/ErrorUtils';

const validationSchema = z
	.object({
		password: z.string().nonempty('Password is required').min(12, 'Password must be at least 12 characters'),
		confirmPassword: z
			.string()
			.nonempty('Confirm password is required')
			.min(12, 'Confirm password must be at least 12 characters'),
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Passwords do not match',
		path: ['confirmPassword'],
	});

export type ResetPasswordFormProps = {
	className?: string;
	onSubmit?: (values: ResetPasswordFormType) => Promise<void>;
	children?: ReactNode;
};

export default function ResetPasswordForm(props: ResetPasswordFormProps) {
	const { className, onSubmit, children } = props;

	const { isLoading, addLoader, removeLoader } = useLoadTracker();
	const [error, setError] = useState<ReactNode | null>(null);
	const [hasSubmitted, setHasSubmitted] = useState(false);

	const form = useForm<ResetPasswordFormType>({
		initialValues: {
			password: '',
			confirmPassword: '',
		},
		validate: schemaResolver(validationSchema),
	});

	const onFormSubmit = async (values: ResetPasswordFormType) => {
		if (!onSubmit) {
			return;
		}

		const loader = addLoader();
		try {
			setError(null);
			await onSubmit(values);
			setHasSubmitted(true);
		} catch (err) {
			if (JsonApiError.isJsonApiError(err) && err.statusCode === 401) {
				setError(
					<>
						This reset link is invalid or has expired. Please request a new one from the{' '}
						<a href="/forgot-password">Forgot password</a> page.
					</>,
				);
			} else if (err instanceof Error) {
				setError(err.message);
			} else if (err) {
				setError(`${err}`);
			} else {
				setError('Unknown error');
			}
		} finally {
			removeLoader(loader);
		}
	};

	return (
		<div className={`ResetPasswordForm ${className ?? ''}`}>
			<Form form={form} onSubmit={onFormSubmit} disabled={isLoading}>
				{hasSubmitted ? (
					<Center>
						<p>Your password was successfully reset!</p>
						<p>
							<a href="/login">Login</a> to continue.
						</p>
					</Center>
				) : (
					<>
						<FormControl name="password" label="New Password">
							<TextInput type="password" placeholder="New Password" />
						</FormControl>
						<FormControl name="confirmPassword" label="Confirm Password">
							<TextInput type="password" placeholder="Confirm Password" />
						</FormControl>
						<Button type="submit" isLoading={isLoading}>
							Reset password
						</Button>
						{error && <p className="error">{error}</p>}
					</>
				)}
				{children}
			</Form>
		</div>
	);
}

export type ResetPasswordFormType = {
	password: string;
	confirmPassword: string;
};
